/**
 * DadosGeraisTab - Hospital general data form tab
 */
import React from 'react';
import { Building2, MapPin, Phone } from 'lucide-react';
import type { Hospital } from '../../hooks/useHospitalData';
import type { Estado, Municipio } from '../../hooks/useGeoData';
import { formatTelefone, formatCEP, formatCNPJ, formatCPF } from '../../hooks/useHospitalForm';

interface DadosGeraisTabProps {
  currentHospital: Hospital | null;
  isEditing: boolean;
  estados: Estado[];
  municipios: Municipio[];
  loadingCep: boolean;
  onFieldChange: (field: keyof Hospital, value: any) => void;
}

export const DadosGeraisTab: React.FC<DadosGeraisTabProps> = ({
  currentHospital,
  isEditing,
  estados,
  municipios,
  loadingCep,
  onFieldChange,
}) => {
  const handleCnpjChange = (value: string) => {
    // Use pessoa type to determine CPF vs CNPJ formatting
    // This avoids suffix digits confusing the auto-detection
    const isCpf = currentHospital?.pessoa === 'FISICA';
    const formatted = isCpf ? formatCPF(value) : formatCNPJ(value);
    onFieldChange('cnpj', formatted);
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm h-full">
      {/* 3-Column Grid */}
      <div className="grid grid-cols-3 gap-4 p-4 h-full">

        {/* COLUMN 1 - IDENTIFICATION (Blue) */}
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 pb-2 border-b border-blue-200 dark:border-blue-800">
            <Building2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-wide">
              Identificação
            </span>
          </div>

          {/* Tipo Pessoa */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Tipo Pessoa</label>
            <div className="flex gap-4 pt-1">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="pessoa"
                  checked={currentHospital?.pessoa === 'FISICA'}
                  onChange={() => onFieldChange('pessoa', 'FISICA')}
                  disabled={!isEditing}
                  className="w-3.5 h-3.5 text-blue-600"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">Física</span>
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="pessoa"
                  checked={currentHospital?.pessoa === 'JURIDICA'}
                  onChange={() => onFieldChange('pessoa', 'JURIDICA')}
                  disabled={!isEditing}
                  className="w-3.5 h-3.5 text-blue-600"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">Jurídica</span>
              </label>
            </div>
          </div>

          {/* Razão Social */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Razão Social <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={currentHospital?.razao_social || ''}
              onChange={(e) => onFieldChange('razao_social', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Nome Fantasia */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nome Fantasia <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={currentHospital?.nome_fantasia || ''}
              onChange={(e) => onFieldChange('nome_fantasia', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* CNPJ/CPF */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">CNPJ/CPF</label>
            <input
              type="text"
              value={currentHospital?.cnpj || ''}
              onChange={(e) => handleCnpjChange(e.target.value)}
              readOnly={!isEditing}
              placeholder="CPF ou CNPJ"
              maxLength={30}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Ativo */}
          <div className="pt-2">
            <label className={`inline-flex items-center ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
              <input
                type="checkbox"
                checked={currentHospital?.ativo || false}
                onChange={(e) => onFieldChange('ativo', e.target.checked)}
                disabled={!isEditing}
                className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-xs font-medium text-gray-700 dark:text-gray-300">Hospital Ativo</span>
            </label>
          </div>
        </div>

        {/* COLUMN 2 - LOCATION (Green) */}
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 pb-2 border-b border-emerald-200 dark:border-emerald-800">
            <MapPin className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wide">
              Localização
            </span>
          </div>

          {/* CEP */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              CEP {loadingCep && <span className="text-blue-500 text-xs ml-1">⏳</span>}
            </label>
            <input
              type="text"
              value={currentHospital?.cep || ''}
              onChange={(e) => onFieldChange('cep', formatCEP(e.target.value))}
              readOnly={!isEditing}
              placeholder="00000-000"
              maxLength={9}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Endereço */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Endereço</label>
            <input
              type="text"
              value={currentHospital?.endereco || ''}
              onChange={(e) => onFieldChange('endereco', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Bairro */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Bairro</label>
            <input
              type="text"
              value={currentHospital?.bairro || ''}
              onChange={(e) => onFieldChange('bairro', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Estado */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Estado</label>
            <select
              value={currentHospital?.estado || ''}
              onChange={(e) => onFieldChange('estado', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100 disabled:opacity-50`}
            >
              <option value="">Selecione...</option>
              {estados.map((estado) => (
                <option key={estado.codigo_uf} value={estado.uf}>{estado.nome}</option>
              ))}
            </select>
          </div>

          {/* Cidade */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cidade</label>
            <select
              value={currentHospital?.cidade || ''}
              onChange={(e) => onFieldChange('cidade', e.target.value)}
              disabled={!isEditing || municipios.length === 0}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100 disabled:opacity-50`}
            >
              <option value="">{municipios.length === 0 ? 'Selecione estado primeiro...' : 'Selecione...'}</option>
              {municipios.map((municipio) => (
                <option key={municipio.codigo_ibge} value={municipio.nome}>{municipio.nome}</option>
              ))}
            </select>
          </div>
        </div>

        {/* COLUMN 3 - CONTACT & CONTROL (Purple) */}
        <div className="space-y-3">
          <div className="flex items-center gap-1.5 pb-2 border-b border-purple-200 dark:border-purple-800">
            <Phone className="w-4 h-4 text-purple-600 dark:text-purple-400" />
            <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
              Contato & Controle
            </span>
          </div>

          {/* Telefone + Ramal */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Telefone</label>
              <input
                type="text"
                value={currentHospital?.telefone || ''}
                onChange={(e) => onFieldChange('telefone', formatTelefone(e.target.value))}
                readOnly={!isEditing}
                placeholder="(00) 00000-0000"
                maxLength={15}
                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                  isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
                } text-gray-900 dark:text-gray-100`}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Ramal</label>
              <input
                type="text"
                value={currentHospital?.ramal || ''}
                onChange={(e) => onFieldChange('ramal', e.target.value)}
                readOnly={!isEditing}
                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                  isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
                } text-gray-900 dark:text-gray-100`}
              />
            </div>
          </div>

          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
            <input
              type="email"
              value={currentHospital?.email || ''}
              onChange={(e) => onFieldChange('email', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100`}
            />
          </div>

          {/* Periodicidade */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Periodicidade</label>
            <select
              value={currentHospital?.periodicidade || ''}
              onChange={(e) => onFieldChange('periodicidade', e.target.value)}
              disabled={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100 disabled:opacity-50`}
            >
              <option value="">Selecione...</option>
              <option value="Semanal">Semanal</option>
              <option value="Quinzenal">Quinzenal</option>
              <option value="Mensal">Mensal</option>
            </select>
          </div>

          {/* Observações */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
            <textarea
              rows={3}
              value={currentHospital?.observacoes || ''}
              onChange={(e) => onFieldChange('observacoes', e.target.value)}
              readOnly={!isEditing}
              className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md ${
                isEditing ? 'bg-white dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-700'
              } text-gray-900 dark:text-gray-100 resize-none`}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
